/**
 * mergeNames.js — Motor del merge histórico de nombres (operadores/guardias).
 *
 * Fusiona N variantes de un nombre en un canónico:
 *  1. Escanea las alarmas del recinto (chunks de 30 zoneIds, patrón Facility.jsx)
 *     y reescribe dispatchedBy / closedBy / guardName / guards[] donde matcheen.
 *  2. Limpia Facilities/{id}.operators|guards (quita variantes, asegura canónico).
 *  3. Registra los aliases en Facilities/{id}.nameAliases.{kind} para que
 *     futuros ingresos se re-mapeen automáticamente (ver utils/names.js).
 *  4. Deja auditoría en la subcolección Facilities/{id}/mergeLog.
 *
 * Idempotente: re-ejecutar el mismo merge es seguro (los docs ya migrados
 * dejan de matchear).
 */
import { db, platform } from "../firebase";
import { schema } from "../schema";
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  writeBatch,
  doc,
  updateDoc,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { nameKey, normalizeName } from "./names";

const BATCH_LIMIT = 450; // margen bajo el límite de 500 writes por batch

function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function commitWithRetry(batch) {
  try {
    await batch.commit();
  } catch (e) {
    console.warn("mergeNames: batch commit falló, reintentando en 1s…", e);
    await sleep(1000);
    await batch.commit(); // segundo intento; si falla, propaga
  }
}

/**
 * Ejecuta la fusión de nombres.
 *
 * Soporta clientes multi-recinto (ej. Copec, donde el supervisor tiene N
 * recintos): recibe la lista de facilityIds y limpia listas/aliases en cada
 * doc afectado. El scan de alarmas usa zonesIds, que el llamador debe pasar
 * abarcando las zonas de TODOS esos recintos.
 *
 * @param {object} p
 * @param {string[]} p.facilityIds — ids de los recintos del supervisor (1+)
 * @param {Array<string|number>} p.zonesIds — ids de zonas (con variantes num/string si aplica)
 * @param {"operators"|"guards"} p.kind
 * @param {string[]} p.variants — variantes a fusionar (valores exactos de la lista)
 * @param {string} p.canonical — nombre final
 * @param {{uid: string, name: string}} p.performedBy
 * @param {(info: {phase: string, chunk: number, totalChunks: number, scanned: number, updated: number}) => void} [p.onProgress]
 * @param {"ui"|"script"} [p.source]
 * @returns {Promise<{scannedDocs: number, updatedDocs: number, facilitiesTouched: number}>}
 */
export async function runNameMerge({
  facilityIds,
  zonesIds,
  kind,
  variants,
  canonical,
  performedBy,
  onProgress = () => {},
  source = "ui",
}) {
  const canonicalClean = normalizeName(canonical);
  const variantKeys = new Set(variants.map(nameKey).filter(Boolean));
  if (!canonicalClean || variantKeys.size === 0) {
    return { scannedDocs: 0, updatedDocs: 0 };
  }

  // Matchea por clave (sin tildes/mayúsculas) pero NO reescribe lo que ya está
  // exactamente igual al canónico. Cubre también el caso "renombrar" donde la
  // variante comparte clave con el canónico (ej. "Jose Perez" → "José Pérez").
  const matches = (val) => !!val && val !== canonicalClean && variantKeys.has(nameKey(val));

  // ── 1. Escaneo por chunks + parcheo en batches ──
  const zoneChunks = chunk(zonesIds, 30);
  let scanned = 0;
  let updated = 0;

  let batch = writeBatch(db);
  let batchCount = 0;
  const flush = async () => {
    if (batchCount > 0) {
      await commitWithRetry(batch);
      batch = writeBatch(db);
      batchCount = 0;
    }
  };

  for (let ci = 0; ci < zoneChunks.length; ci++) {
    onProgress({ phase: "scan", chunk: ci + 1, totalChunks: zoneChunks.length, scanned, updated });
    const q = query(
      collection(db, schema.groupedAlarms),
      where(schema.zoneField, "in", zoneChunks[ci])
    );
    const snap = await getDocs(q);
    scanned += snap.docs.length;

    for (const d of snap.docs) {
      const data = d.data();
      const patch = {};
      if (matches(data.dispatchedBy)) patch.dispatchedBy = canonicalClean;
      if (matches(data.closedBy)) patch.closedBy = canonicalClean;
      if (matches(data.guardName)) patch.guardName = canonicalClean;
      if (Array.isArray(data.guards) && data.guards.some(matches)) {
        // Reemplaza variantes por el canónico y deduplica por clave
        const seen = new Set();
        patch.guards = data.guards
          .map((g) => (matches(g) ? canonicalClean : g))
          .filter((g) => {
            const k = nameKey(g);
            if (!k || seen.has(k)) return false;
            seen.add(k);
            return true;
          });
      }
      if (Object.keys(patch).length > 0) {
        batch.update(doc(db, schema.groupedAlarms, d.id), patch);
        batchCount++;
        updated++;
        if (batchCount >= BATCH_LIMIT) await flush();
      }
    }
    onProgress({ phase: "scan", chunk: ci + 1, totalChunks: zoneChunks.length, scanned, updated });
  }
  await flush();

  // ── 2. Limpiar listas + fusionar aliases en cada recinto afectado ──
  onProgress({ phase: "facility", chunk: zoneChunks.length, totalChunks: zoneChunks.length, scanned, updated });
  const canonKey = nameKey(canonicalClean);
  let facilitiesTouched = 0;

  for (const facilityId of facilityIds) {
    // Leemos las listas frescas de cada recinto para evitar carreras.
    const facRef = doc(db, `Facilities/${facilityId}`);
    const facSnap = await getDoc(facRef);
    if (!facSnap.exists()) continue;
    const facData = facSnap.data();

    const currentList = Array.isArray(facData[kind]) ? facData[kind] : [];
    // ¿La persona figura en la lista de este recinto (alguna variante o el canónico)?
    // Nota: puede NO figurar y aun así existir en los registros de alarmas — en
    // ese caso no tocamos la lista, pero sí registramos los aliases para que
    // futuros ingresos se re-mapeen.
    const inList = currentList.some((n) => {
      const k = nameKey(n);
      return k === canonKey || variantKeys.has(k);
    });

    const patch = {};

    if (inList) {
      const seenKeys = new Set();
      const newList = [];
      // El canónico primero, luego el resto (sin variantes ni duplicados por clave)
      for (const n of [canonicalClean, ...currentList]) {
        const k = nameKey(n);
        if (!k || seenKeys.has(k)) continue;
        if (k !== canonKey && variantKeys.has(k)) continue; // es una variante fusionada
        seenKeys.add(k);
        newList.push(k === canonKey ? canonicalClean : n);
      }
      patch[kind] = newList;
    }

    // Fusionar aliases (siempre), aplanando cadenas: toda entrada previa cuyo
    // VALOR sea una variante fusionada pasa a apuntar al nuevo canónico.
    const prevAliases = facData.nameAliases?.[kind] || {};
    const mergedAliases = {};
    for (const [k, v] of Object.entries(prevAliases)) {
      mergedAliases[k] = variantKeys.has(nameKey(v)) ? canonicalClean : v;
    }
    for (const vk of variantKeys) mergedAliases[vk] = canonicalClean;
    mergedAliases[canonKey] = canonicalClean; // autocura tildes/mayúsculas futuras
    // Objeto completo (nunca dot-paths: las claves pueden contener puntos)
    patch.nameAliases = { ...(facData.nameAliases || {}), [kind]: mergedAliases };

    await updateDoc(facRef, patch);

    // ── 3. Auditoría por recinto ──
    await addDoc(collection(db, `Facilities/${facilityId}/mergeLog`), {
      kind,
      variants: [...variants],
      canonical: canonicalClean,
      scannedDocs: scanned,
      updatedDocs: updated,
      listChanged: inList,
      performedByUID: performedBy?.uid || null,
      performedByName: performedBy?.name || null,
      source,
      platform,
      createdAt: serverTimestamp(),
    });
    facilitiesTouched++;
  }

  onProgress({ phase: "done", chunk: zoneChunks.length, totalChunks: zoneChunks.length, scanned, updated });
  return { scannedDocs: scanned, updatedDocs: updated, facilitiesTouched };
}
